import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { IconButton, TableCell, TableRow } from '@mui/material';
import { onAutocompleteChangeInForm } from '@sharedCR/common/form';
import { AutocompleteCDS, DataFieldCDS, DateFieldCDS } from '@sharedCR/coreDesignSystem/form';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { DeleteIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import dayjs from 'dayjs';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { FieldInputType } from 'constants/fields';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { isFieldValueChanged } from 'utils/loanFees';

import {
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';
import { selectFormHiddenPage } from 'store/loanApplications/selectors/lockedFields.selectors';

import { LoanFeesTestIds } from '../../../../../../LoanFees.testIds';

import { CALENDAR_DATE_FORMAT } from './AdjustmentsTableRow.constants';

const AdjustmentsTableRow = ({
  isLoading,
  validationErrors,
  adjustmentId,
  index,
  handleChange,
  validateField,
  setInForm,
  setForm,
  validateForm,
  adjustmentTypeOptions,
  adjustmentTypeOptionsNotDeleted,
  onDelete,
  formDatasetName,
  ...adjustment
}) => {
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.FEES));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.FEES));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.FEES));
  const isPageHidden = useSelector(selectFormHiddenPage(FormNames.FEES));

  const isDisabledByLock = isLoading || isPageLocked;

  const onAdjustmentNameChange = useCallback(
    index => (e, value) => {
      onAutocompleteChangeInForm({ validateField, setInForm })(e, value);

      setForm(prev => {
        const clonePrev = _.cloneDeep(prev);
        clonePrev.set_adjustments[index].sellerAdjustmentTypeId = value;

        return clonePrev;
      });
    },
    [setForm, setInForm, validateField],
  );

  const onDateChange = useCallback(
    (value, fieldName) => {
      const date = dayjs(value).isValid() ? dayjs.utc(value).format(CALENDAR_DATE_FORMAT) : null;

      setInForm(fieldName, date);
      validateField(fieldName, date);
    },
    [setInForm, validateField],
  );

  if (adjustment.pendingChangesActionType === PendingChangesActionType.DELETED || isPageHidden) {
    return null;
  }

  return (
    <TableRow className={`table-row-pending-${adjustment.pendingChangesActionType}`}>
      <TableCell>
        <AutocompleteCDS
          fullWidth
          withPopupError
          dataTestId={LoanFeesTestIds.getAdjustmentTypeSelectId(adjustmentId)}
          disabled={isDisabledByLock || lockedFieldsMap.sellerAdjustmentTypeId}
          error={!!_.get(validationErrors, `${formDatasetName}[${index}].sellerAdjustmentTypeId`)}
          helperText={_.get(
            validationErrors,
            `${formDatasetName}[${index}].sellerAdjustmentTypeId`,
          )}
          id={`${formDatasetName}[${index}].sellerAdjustmentTypeId`}
          isFieldChanged={isFieldValueChanged(adjustment, 'sellerAdjustmentTypeId')}
          isHidden={hiddenFieldsMap.sellerAdjustmentTypeId}
          name={`${formDatasetName}[${index}].sellerAdjustmentTypeId`}
          options={
            adjustment.isDeletedType ? adjustmentTypeOptions : adjustmentTypeOptionsNotDeleted
          }
          size="small"
          value={adjustment.sellerAdjustmentTypeId}
          onChange={onAdjustmentNameChange(index)}
        />
      </TableCell>

      <TableCell>
        <DateFieldCDS
          clearable
          fullWidth
          withPopupError
          dataTestId={LoanFeesTestIds.getAdjustmentStartDateId(adjustmentId)}
          disabled={isDisabledByLock || lockedFieldsMap.startDate}
          error={!!_.get(validationErrors, `${formDatasetName}[${index}].startDate`)}
          helperText={_.get(validationErrors, `${formDatasetName}[${index}].startDate`)}
          id={`${formDatasetName}[${index}].startDate`}
          isFieldChanged={isFieldValueChanged(adjustment, 'startDate')}
          isHidden={hiddenFieldsMap.startDate}
          label=""
          maxDate={dayjs(adjustment.endDate) || undefined}
          name={`${formDatasetName}[${index}].startDate`}
          size="small"
          value={adjustment.startDate}
          onBlur={_.noop}
          onChange={onDateChange}
          onFocus={_.noop}
        />
      </TableCell>

      <TableCell>
        <DateFieldCDS
          clearable
          fullWidth
          withPopupError
          dataTestId={LoanFeesTestIds.getAdjustmentEndDateId(adjustmentId)}
          disabled={isDisabledByLock || lockedFieldsMap.endDate}
          error={!!_.get(validationErrors, `${formDatasetName}[${index}].endDate`)}
          helperText={_.get(validationErrors, `${formDatasetName}[${index}].endDate`)}
          id={`${formDatasetName}[${index}].endDate`}
          isFieldChanged={isFieldValueChanged(adjustment, 'endDate')}
          isHidden={hiddenFieldsMap.endDate}
          label=""
          minDate={dayjs(adjustment.startDate) || undefined}
          name={`${formDatasetName}[${index}].endDate`}
          size="small"
          value={adjustment.endDate}
          onBlur={_.noop}
          onChange={onDateChange}
          onFocus={_.noop}
        />
      </TableCell>

      <TableCell>
        <DataFieldCDS
          fullWidth
          withPopupError
          data-is-amount="true"
          disabled={isDisabledByLock || lockedFieldsMap.totalAmount}
          error={!!_.get(validationErrors, `${formDatasetName}[${index}].totalAmount`)}
          helperText={_.get(validationErrors, `${formDatasetName}[${index}].totalAmount`)}
          id={`${formDatasetName}[${index}].totalAmount`}
          isFieldChanged={isFieldValueChanged(adjustment, 'totalAmount')}
          isHidden={hiddenFieldsMap.totalAmount}
          name={`${formDatasetName}[${index}].totalAmount`}
          size="small"
          type={FieldInputType.CURRENCY}
          value={adjustment.totalAmount}
          onChange={handleChange}
        />
      </TableCell>

      <TableCell align="center">
        <IconButton
          disableRipple
          disableTouchRipple
          className="label-button"
          data-adjustment-id={adjustmentId}
          data-testid={LoanFeesTestIds.getAdjustmentDeleteButtonId(adjustmentId)}
          disabled={isLoading}
          onClick={onDelete}
        >
          <DeleteIcon size={IconSize[20]} />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

AdjustmentsTableRow.propTypes = {
  ...WithLoadingPropTypes,
  validationErrors: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  adjustmentId: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  setInForm: PropTypes.func.isRequired,
  setForm: PropTypes.func.isRequired,
  pendingChangesActionType: PropTypes.string.isRequired,
  validateForm: PropTypes.func.isRequired,
  adjustmentTypeOptions: PropTypes.array.isRequired,
  formDatasetName: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default AdjustmentsTableRow;
